import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, ViewChildren, AfterViewInit, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';
import { BillingService } from '../../../services/billing.service';

@Component({
    selector: 'billing-history-bills-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; display:block; }'],
    host: { 'class': 'block-detail' }
})
export class BillingHistoryBillsChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    heatGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number = 0;
    historyBill: any;
    hasNumberOfBills: boolean = false;
    hasTotalValue: boolean = false;

    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(
        private billingService: BillingService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngAfterViewInit(): void {
        this.subscriptions.push(this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        }));
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId') ? getQueryParamFromAncestorRoute(this.route, 'siteId') : 0;
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {

        this.historyBill = [];
        this.billingService.getBillHistory(this.clientId, this.siteId, this.timePeriod).then().then((result) => {
            this.historyBill = result;
            this.chartData = this.processData(this.historyBill);
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            this.createChart();
        }).catch(reason => {
            this.hasDataChange.emit(false);
            this.hasLoadingChange.emit(false);
        });
    }

    processData(data: any[]) {

        let monthCount: number = 0;
        this.hasNumberOfBills = false;
        this.hasTotalValue = false;


        let graphData = {
            labels: [],
            numberOfBills: [],
            totalValue: [],
        };

        if (data && data.length > 0) graphData.labels.push(this.pipe.transform(data[0].date, 'MMM'));

        var initializeMonth = function () {
            graphData.numberOfBills.push(0);
            graphData.totalValue.push(0);
        }

        initializeMonth();


        for (let i = 0; i < data.length; i++) {


            if (data[i].numberOfBills > 0) {
                graphData.numberOfBills[monthCount] = data[i].numberOfBills;
                this.hasNumberOfBills = true;
            }


            if (data[i].totalValue > 0) {
                graphData.totalValue[monthCount] = data[i].totalValue;
                this.hasTotalValue = true;
            }

            if (i + 1 < data.length && data[i].date != data[i + 1].date) {
                graphData.labels.push(this.pipe.transform(data[i + 1].date, 'MMM'));
                initializeMonth();
                monthCount++;
            }
        }

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'bar',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            if (tooltipItem.datasetIndex == 1) { return hoverData.label + ' ' + tooltipData;}
                            return hoverData.label + ' £' + tooltipData;
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        barThickness: 20,
                        scaleLabel: {
                            display: true,
                            labelString: 'Month',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        gridLines: {
                            display: false
                        }
                    }],
                    yAxes: [
                        {
                            id: "y-axis-0",
                            position: 'left',
                            ticks: {
                                beginAtZero: true
                            },
                            scaleLabel: {
                                display: true,
                                labelString: 'Value (£)',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: true
                            }
                        },
                        {
                            id: "y-axis-1",
                            position: 'right',
                            ticks: {
                                beginAtZero: true
                            },
                            scaleLabel: {
                                display: true,
                                labelString: 'Quantity',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: false
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        if (this.hasTotalValue) {
            datasets.push({
                type: 'bar',
                label: 'Billed value',
                yAxisID: 'y-axis-0',
                data: this.chartData.totalValue,
                backgroundColor: '#153954'
            });
        }

        if (this.hasNumberOfBills) {
            datasets.push({
                type: 'line',
                fill: false,
                label: 'No. of bills',
                yAxisID: 'y-axis-1',
                data: this.chartData.numberOfBills,
                backgroundColor: '#009aa3',
                borderColor: '#009aa3',
                pointBorderColor: '#009aa3',
                pointBackgroundColor: '#009aa3',
                pointHoverBackgroundColor: '#009aa3',
            });
        }

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets,
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
